import { STEPS } from '@/features/studio-renew/steps'

import type { Character, Chat } from '@/features/studio-renew/types'

export type CompletionTarget = {
  kind: 'work' | 'character'
  id: string
  step: string
  startId?: string
}
export type CompletionItem = {
  id: string
  label: string
  done: boolean
  target: CompletionTarget
  hint?: string
  points?: number
}
/** field 는 그 칸의 Field 라벨, message 는 그 칸으로 데려갈 때 띄우는 안내 */
export type RequiredItem = CompletionItem & { field: string; message: string }
export const PUBLISH_COMPLETION = 60

/** 필수 항목은 60점, 선택 항목은 40점. 선택 항목으로 게시 기준을 대신 채울 수 없다. */
export function workCompletion(
  chat: Chat,
  characters: Record<string, Character>
) {
  const work = (step: string): CompletionTarget => ({
    kind: 'work',
    id: chat.id,
    step,
  })
  const cast = chat.characterIds.map((id) => characters[id])
  const required: RequiredItem[] = [
    {
      id: 'cast',
      label: '출연 캐릭터',
      done: cast.length > 0 && cast.every(Boolean),
      target: work('cast'),
      field: '출연 캐릭터',
      message: '캐릭터를 추가해 주세요.',
    },
    {
      id: 'lead',
      label: '주인공',
      done: !!chat.lead && chat.characterIds.includes(chat.lead),
      target: work('cast'),
      field: '출연 캐릭터',
      message: '주인공을 지정해 주세요.',
    },
  ]
  chat.characterIds.forEach((id, index) => {
    const character = characters[id]
    const prefix =
      cast.length > 1
        ? `${character?.name || `캐릭터 ${index + 1}`} · `
        : '캐릭터 '
    const target = (step: string): CompletionTarget => ({
      kind: 'character',
      id,
      step,
    })
    required.push(
      {
        id: `${id}:name`,
        label: `${prefix}이름`,
        done: !!character?.name.trim(),
        target: target('profile'),
        field: '이름',
        message: '캐릭터 이름을 입력해 주세요.',
      },
      {
        id: `${id}:image`,
        label: `${prefix}대표 이미지`,
        done: !!character?.image.trim(),
        target: target('profile'),
        field: '대표 이미지',
        message: '대표 이미지를 올려 주세요.',
      },
      {
        id: `${id}:tagline`,
        label: `${prefix}한 줄 소개`,
        done: !!character?.tagline.trim(),
        target: target('profile'),
        field: '한 줄 소개',
        message: '한 줄 소개를 입력해 주세요.',
      },
      {
        id: `${id}:persona`,
        label: `${prefix}설정`,
        done: !!character?.persona.trim(),
        target: target('persona'),
        field: '캐릭터 설정',
        message: '캐릭터 설정을 입력해 주세요.',
      }
    )
  })
  if (cast.length > 1)
    required.push({
      id: 'name',
      label: '작품 이름 (2자 이상)',
      done: chat.name.trim().length >= 2,
      target: work('intro'),
      field: '작품 이름',
      message: '작품 이름을 2자 이상 입력해 주세요.',
    })
  const starts = chat.starts.length ? chat.starts : [null]
  starts.forEach((start, index) => {
    const prefix = starts.length > 1 ? `시작 장면 ${index + 1}` : '시작 장면'
    const target = { ...work('starts'), startId: start?.id }
    required.push(
      {
        id: `start:${index}:title`,
        label: `${prefix} 제목`,
        done: !!start?.name.trim(),
        target,
        field: '시작 장면 제목',
        message: '시작 장면 제목을 입력해 주세요.',
      },
      {
        id: `start:${index}:speech`,
        label: `${prefix} 인트로`,
        done: !!start?.intro.lines.some(
          (line) => line.who === 'char' && !!line.text.trim()
        ),
        target,
        field: '인트로',
        message: '인트로를 입력해 주세요.',
      }
    )
  })
  required.push(
    {
      id: 'description',
      label: '작품 소개',
      done: !!chat.description.trim(),
      target: work('intro'),
      field: '작품 소개',
      message: '작품 소개를 입력해 주세요.',
    },
    {
      id: 'tags',
      label: '태그',
      done: chat.tags.length > 0,
      target: work('intro'),
      field: '태그',
      message: '태그를 1개 이상 입력해 주세요.',
    },
    {
      id: 'audience',
      label: '이용 연령',
      done: !!chat.audience,
      target: work('publish'),
      field: '이용 연령',
      message: '이용 연령을 선택해 주세요.',
    },
    {
      id: 'consent',
      label: '작품 삭제 후 처리 동의',
      done: chat.consent,
      target: work('publish'),
      field: '작품 삭제 후 처리',
      message: '작품 삭제 후 처리에 동의해 주세요.',
    }
  )
  const lead = characters[chat.lead || '']
  const optional: CompletionItem[] = [
    {
      id: 'story',
      label: '스토리',
      done: !!chat.situation?.trim(),
      points: 12,
      target: work('album'),
      hint: '이야기의 배경과 사건을 적으면 캐릭터가 대화의 방향을 참고할 수 있어요.',
    },
    {
      id: 'context',
      label: '숨은 상황',
      done:
        !!chat.starts.length &&
        chat.starts.every((start) => !!start.intro.context.trim()),
      points: 6,
      target: work('starts'),
      hint: '캐릭터만 알고 시작할 상황을 적어 인트로의 맥락을 잡아 주세요.',
    },
    {
      id: 'photos',
      label: '캐릭터 상황 이미지',
      done: !!lead?.album?.some(
        (shot) => !!shot.image.trim() && !!shot.situation?.trim()
      ),
      points: 6,
      target: lead
        ? { kind: 'character', id: lead.id, step: 'scenes' }
        : work('cast'),
      hint: '대표 이미지 외에 상황에 맞는 모습 표시',
    },
    {
      id: 'suggestions',
      label: '추천 답변',
      done:
        !!chat.starts.length &&
        chat.starts.every((start) =>
          start.suggestions.some((text) => !!text.trim())
        ),
      points: 4,
      target: work('starts'),
      hint: '첫 답장을 망설이는 이용자에게 대화의 실마리를 줄 수 있어요.',
    },
    {
      id: 'guide',
      label: '플레이 가이드',
      done: !!chat.playGuide.trim(),
      points: 4,
      target: work('album'),
      hint: '이 작품을 즐기는 방법을 짧게 안내해 주세요.',
    },
    {
      id: 'status',
      label: '상태창',
      done: !!chat.statusId,
      points: 4,
      target: work('status'),
      hint: '대화 중 바뀌는 호감도나 장소 등을 보여 줄 수 있어요.',
    },
    {
      id: 'commands',
      label: '명령어',
      done: chat.commandIds.length + chat.officialCommands.length > 0,
      points: 4,
      target: work('commands'),
      hint: '대화 중 사용할 특별한 기능을 골라 연결해 보세요.',
    },
  ]
  const ready =
    required.every((item) => item.done) &&
    STEPS.work
      .filter((step) => step.required)
      .every((step) => step.done({ chat, characters }))
  const requiredPoints = Math.floor(
    (PUBLISH_COMPLETION * required.filter((item) => item.done).length) /
      required.length
  )
  const optionalPoints = optional.reduce(
    (sum, item) => sum + (item.done ? (item.points ?? 0) : 0),
    0
  )
  return {
    required,
    optional,
    ready,
    percent: ready
      ? PUBLISH_COMPLETION + optionalPoints
      : Math.min(PUBLISH_COMPLETION - 1, requiredPoints + optionalPoints),
  }
}
